import type {Property} from "@/src/modules/properties/types";

import type {BriefingAudienceOptions} from "../types";

export const BRIEFING_PAID_PROPERTIES_QUERY_LIMIT = 500;

/** Paid managed sites — excludes hidden/system properties (shift create uses `is_hidden: 0`). */
export const BRIEFING_PROPERTY_FILTER = {
  status: 1,
  is_hidden: 0 as const,
  paid: 1,
};

export type BriefingRoleOption = {key: string; name: string};

/** Fixed briefing targeting allowlist — must match API `role_keys` values (A–Z by label). */
export const BRIEFING_ALLOWED_ROLES: readonly BriefingRoleOption[] = [
  {key: "dispatch", name: "Dispatch"},
  {key: "dispatch_supervisor", name: "Dispatch Supervisor"},
  {key: "officer", name: "Officer"},
  {key: "patrol_officer", name: "Patrol Officer"},
  {key: "post_commander", name: "Post Commander"},
  {key: "senior_supervisor", name: "Senior Supervisor"},
  {key: "supervisor", name: "Supervisor"},
] as const;

export function getBriefingAllowedRoles(): BriefingRoleOption[] {
  return [...BRIEFING_ALLOWED_ROLES].toSorted((a, b) => a.name.localeCompare(b.name));
}

export const BRIEFING_ALLOWED_ROLE_KEYS = new Set(
  BRIEFING_ALLOWED_ROLES.map((role) => role.key),
);

export function formatBriefingRoleLabel(key: string): string {
  const match = BRIEFING_ALLOWED_ROLES.find((role) => role.key === key);
  if (match) {
    return match.name;
  }
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function mapPropertyToBriefingOption(property: Pick<Property, "id" | "name">) {
  return {
    property_id: property.id,
    name: property.name,
  };
}

export function buildBriefingAudienceOptions(
  properties: Pick<Property, "id" | "name">[],
): BriefingAudienceOptions {
  const propertyOptions = properties
    .map(mapPropertyToBriefingOption)
    .toSorted((a, b) => a.name.localeCompare(b.name));

  return {
    properties: propertyOptions,
    roles: getBriefingAllowedRoles(),
  };
}
